import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Title from '../forms/Title';
import { between } from '../utility/functions';
import Icon from '../forms/Icon';
import Row from '../layout/Row';
import RoleProtected, { IsAuthorized, userRoles } from '../RoleProtected';
import TooltipHelper from '../forms/TooltipHelper';
import { SCREEN_WIDTH } from '../../constants/config';

const Wrapper = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    gap: 0.5em;
    z-index: ${({ isDragging }) => isDragging ? 3 : 2};
    transition: all ${({ isDragging }) => isDragging ? 0 : 0.2}s;
    overflow: hidden;
    font-size: ${({ fontSize }) => fontSize}em;
`

const Guide = styled.div`
    position: absolute;
    background: ${({ theme }) => theme.active}30;
    pointer-event: none;
    transition: all 0.2s;
    opacity: ${({ isDragging, isResizing }) => (isDragging || isResizing) ? 1 : 0};
    z-index:  ${({ isResizing }) => isResizing ? 3 : 1};
`

const TileWrapper = styled.div`
    position: relative;
    margin: 0.5em;
    display: flex;
    flex-direction: column;
    gap: 0.5em;
    background: ${({ theme }) => theme.background};
    border-radius: 1em;
    height: calc(100% - 1em);
    width: calc(100% - 1em);
    box-shadow: 0px 3px 10px ${({ theme }) => theme.shadow};
    border: 1px solid ${({ theme }) => theme.gray};
    overflow: hidden;
`

const Content = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 0.5em;
    padding: 1em;
    overflow-x: hidden;
    overflow-y: auto;
    height: 100%;
`

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: all 0.2s;
    min-height: 3em;
    padding: 0 1em;
    cursor: ${({ isDragging }) => isDragging ? 'grabbing' : 'grab'};
    user-select: none;
    z-index: 1;
`

const Arrow = styled.div`
    content: "";
    position: absolute;
    right: 10px;
    bottom: 10px;
    width: 1em;
    height: 1em;
    opacity: ${({ isResizing }) => isResizing ? '1' : '0.2'};
    border-right: 2px solid ${({ theme }) => theme.gray};
    border-bottom: 2px solid ${({ theme }) => theme.gray};
    cursor: nw-resize;
    z-index: 3;
    transition: opacity 0.3s;

    user-select: none;
`

const TitleCss = styled(Title)`
    font-size: 1.2em;
    margin: 0;
`

const Close = styled(Icon)`
    cursor: pointer;
    opacity: ${({ pinned }) => pinned ? 1 : 0.5};
    background: ${({ theme, pinned }) => pinned ? theme.gray + '40' : ''};
    transition: opacity 0.3s;
    padding: 0.2em;
    border-radius: 0.2em;
    width: 1em;
    height: 1em;

    transition: all 0.3s;

    &:hover {
        opacity: 1;
    }
`

const Tile = ({ tilePack, tileId, onClose, children, ...props }) => {
    const tileRef = useRef();
    const { updateLayout, layout, cells, cellSize, boardRef } = tilePack;

    const initialState = {
        dimensions: {
            x: SCREEN_WIDTH ? cellSize : (cells / 4) * cellSize,
            y: SCREEN_WIDTH ? cellSize : (cells / 4) * cellSize,
        },
        guideDimensions: {
            x: SCREEN_WIDTH ? cells : layout[tileId]?.dimensions.x ?? cells / 4,
            y: SCREEN_WIDTH ? cells : layout[tileId]?.dimensions.y ?? cells / 4,
        },
        position: { x: 0, y: 0 },
        pinned: layout[tileId]?.pinned ?? false,
        fontSize: layout[tileId]?.fontSize ?? 0.8,
        guidePos: {
            x: layout[tileId]?.position.x ?? 0,
            y: layout[tileId]?.position.y ?? 0,
        },
        clickOffset: { x: 0, y: 0 },

        isDragging: false,
        isResizing: false,
        firstRun: true,
    };

    const [state, setState] = useState(initialState);

    const { dimensions, guideDimensions, position, pinned, fontSize, guidePos, clickOffset, isDragging, isResizing, firstRun } = state;

    useEffect(() => {
        if (firstRun) {
            setState((prevState) => ({ ...prevState, firstRun: false }));
            return;
        }

        const handleMouseUp = () => {
            setState((prevState) => ({
                ...prevState,
                isDragging: false,
                isResizing: false,
            }));
        };

        const handleMouseMove = (event) => {
            if (isDragging) {
                const parentRect = boardRef.current.getBoundingClientRect();
                const offsetX = event.clientX - parentRect.left - clickOffset.x + boardRef.current.scrollLeft;
                const offsetY = event.clientY - parentRect.top - clickOffset.y + boardRef.current.scrollTop;
                setState((prevState) => ({
                    ...prevState,
                    position: { x: offsetX, y: offsetY },
                }));
            }

            if (isResizing) {
                const offsetX = tileRef.current.getBoundingClientRect().left;
                const offsetY = tileRef.current.getBoundingClientRect().top;

                setState((prevState) => ({
                    ...prevState,
                    dimensions: {
                        // x: Math.max(cellSize * 10, event.clientX - offsetX) + (cellSize / 2),
                        // y: Math.max(cellSize * 6, event.clientY - offsetY) + (cellSize / 2)
                        x: Math.max(0, event.clientX - offsetX) + (cellSize / 2),
                        y: Math.max(0, event.clientY - offsetY) + (cellSize / 2)
                    },
                }));
            }
        };

        if (isDragging || isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            setState((prevState) => ({
                ...prevState,
                position: { x: guidePos.x * cellSize, y: guidePos.y * cellSize },
                dimensions: { x: guideDimensions.x * cellSize, y: guideDimensions.y * cellSize },
            }));
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing, guidePos, guideDimensions, cellSize, clickOffset, firstRun, boardRef]);


    useEffect(() => {
        if (!isDragging && !isResizing) return;

        const x = between(Math.round(position.x / cellSize), 0, cells - guideDimensions.x);
        const y = Math.max(0, Math.round(position.y / cellSize));

        if (isNaN(x) || isNaN(y)) return;
        if (x !== guidePos.x || y !== guidePos.y) {
            setState((prevState) => ({
                ...prevState,
                guidePos: {
                    x: x,
                    y: y,
                },
            }));
        }
    }, [position, guidePos, guideDimensions, cellSize]);

    useEffect(() => {
        if (!isDragging && !isResizing) return;

        const x = between(Math.round(dimensions.x / cellSize), cells / 16, cells - guidePos.x);
        const y = Math.max(cells / 16, Math.round(dimensions.y / cellSize));

        if (!x || !y) return;
        if (x !== guideDimensions.x || y !== guideDimensions.y) {
            setState((prevState) => ({
                ...prevState,
                guideDimensions: {
                    x: x,
                    y: y,
                },
            }));
        }
    }, [dimensions, guideDimensions, guidePos, cellSize]);

    useEffect(() => {
        if (guideDimensions.x === 0 || guideDimensions.y === 0) return;
        if (isDragging || isResizing || firstRun) {
            updateLayout(tileId, guidePos, guideDimensions, pinned, fontSize);
        }
    }, [guidePos, guideDimensions]);

    useEffect(() => {
        if (!layout[tileId]) return;
        if (isDragging || isResizing) return;

        const { position, dimensions } = layout[tileId];

        if (layout[tileId].pinned !== pinned) {
            setState((e) => ({
                ...e,
                pinned: layout[tileId].pinned,
            }));
        }
        if (position.x !== guidePos.x || position.y !== guidePos.y) {
            setState((e) => ({
                ...e,
                guidePos: { x: position.x, y: position.y },
            }));
        }
        if (dimensions.x !== guideDimensions.x || dimensions.y !== guideDimensions.y) {
            setState((e) => ({
                ...e,
                guideDimensions: { x: dimensions.x, y: dimensions.y },
            }));
        }
    }, [layout, tileId, isDragging, isResizing, cellSize]);

    const handleMouseDown = (e) => {
        e.preventDefault();
        const offsetX = e.clientX - e.currentTarget.getBoundingClientRect().left;
        const offsetY = e.clientY - e.currentTarget.getBoundingClientRect().top;
        setState((prevState) => ({
            ...prevState,
            isDragging: true,
            clickOffset: {
                x: offsetX,
                y: offsetY,
            },
        }));
    };

    const closeTile = () => {
        setState((prevState) => ({
            ...prevState,
            dimensions: {
                x: 0,
                y: 0,
            },
        }));
        setTimeout(() => {
            onClose(tileId)
        }, 200)
    };

    const togglePin = () => {
        setState((prevState) => ({
            ...prevState,
            pinned: !pinned,
        }));
        updateLayout(tileId, guidePos, guideDimensions, !pinned, fontSize);
    };

    const addSize = () => {
        setState((prevState) => ({
            ...prevState,
            fontSize: between(prevState.fontSize + 0.1, 0.5, 1.2),
        }));
        updateLayout(tileId, guidePos, guideDimensions, pinned, fontSize);
    };

    const minusSize = () => {
        setState((prevState) => ({
            ...prevState,
            fontSize: between(prevState.fontSize - 0.1, 0.5, 1.2),
        }));
        updateLayout(tileId, guidePos, guideDimensions, pinned, fontSize);
    };

    const TileStyle = {
        transform: `translate(${position.x}px, ${position.y}px)`,
        width: `${dimensions.x}px`,
        height: `${dimensions.y}px`,
    };

    const GuideStyle = {
        transform: `translate(${guidePos.x * cellSize}px, ${guidePos.y * cellSize}px)`,
        width: `${guideDimensions.x * cellSize}px`,
        height: `${guideDimensions.y * cellSize}px`,
    };

    return (
        <>
            <Wrapper
                ref={tileRef}
                className="draggable"
                style={TileStyle}
                isDragging={isDragging || isResizing}
                fontSize={fontSize}
                {...props}
            >
                <TileWrapper>
                    <RoleProtected roles={userRoles.editDashboard}>
                        <Header isDragging={isDragging} onMouseDown={handleMouseDown}>
                            <TitleCss>{props.label}</TitleCss>
                            {!SCREEN_WIDTH && (
                                <Row>
                                    <TooltipHelper formName={'dashboard'} name={tileId} />
                                    {fontSize < 1.2 && <Close icon='plus' onClick={addSize} />}
                                    {fontSize > 0.5 && <Close icon='minus' onClick={minusSize} />}
                                    <Close icon='pin' pinned={pinned} onClick={togglePin} />
                                    <Close icon='circleCross' onClick={closeTile} />
                                </Row>
                            )}
                        </Header>
                    </RoleProtected>

                    {!IsAuthorized(userRoles.editDashboard) && <Row style={{ padding: '0.5em' }}><TitleCss>{props.label}</TitleCss></Row>}

                    <Content>
                        {children}
                    </Content>

                    {IsAuthorized(userRoles.editDashboard) && (
                        <Arrow
                            isResizing={isResizing}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setState((prevState) => ({ ...prevState, isResizing: true }))
                            }}
                        />
                    )}
                </TileWrapper>
            </Wrapper>
            <Guide isDragging={isDragging} isResizing={isResizing} style={GuideStyle} />
        </>
    );
}

export default Tile;
